import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Line, ComposedChart } from "recharts";
import { TrendingUp, AlertTriangle } from "lucide-react";
import api, { formatCurrency } from "@/lib/api";

/**
 * Gráfica agregada del pulso financiero global — cobros vs pagos próximos 6 meses
 * sobre todas las obras visibles. Línea adicional = neto mensual.
 */
export default function DashboardCashflowChart() {
  const [data, setData] = useState(null);
  const [meses, setMeses] = useState(6);

  useEffect(() => {
    api.get(`/dashboard/cashflow_global?meses=${meses}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [meses]);

  if (!data) return null;

  const chartData = (data.meses || []).map((m) => ({
    mes: m.label,
    Cobros: m.cobros,
    Pagos: m.pagos,
    Neto: m.neto,
  }));

  const noData = chartData.every((d) => d.Cobros === 0 && d.Pagos === 0);

  const fmtAxis = (v) => {
    if (Math.abs(v) >= 1000) return `${Math.round(v / 1000)}k`;
    return v;
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mb-8 md:mb-10"
      data-testid="dashboard-cashflow-chart"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg flex items-center justify-center bg-[var(--legatum-accent)]/15 text-[var(--legatum-accent)] border border-[var(--legatum-accent)]/30">
            <TrendingUp className="h-3.5 w-3.5" />
          </div>
          <h2 className="text-sm font-extrabold uppercase tracking-[0.2em] text-[var(--legatum-text-muted)]">
            Pulso financiero · próximos {meses} meses
          </h2>
        </div>
        <div className="inline-flex rounded-lg border border-[var(--legatum-border)] bg-[var(--legatum-surface)] p-1 text-[10px] font-bold uppercase tracking-widest">
          {[3, 6, 12].map((m) => (
            <button
              key={m}
              onClick={() => setMeses(m)}
              data-testid={`cashflow-horizon-${m}`}
              className={`px-2.5 py-1 rounded-md transition-colors ${
                meses === m
                  ? "bg-[var(--legatum-accent)] text-[var(--legatum-accent-fg)]"
                  : "text-[var(--legatum-text-muted)] hover:text-[var(--legatum-text)]"
              }`}
            >
              {m}M
            </button>
          ))}
        </div>
      </div>

      <div
        className="rounded-2xl p-4 md:p-5"
        style={{
          background: "var(--legatum-surface)",
          border: "1px solid var(--legatum-border)",
        }}
      >
        {/* Mini KPIs superiores */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="rounded-lg p-3 bg-[var(--legatum-elevated)] border border-[var(--legatum-border)]">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--legatum-text-muted)]">Total cobros</div>
            <div className="mt-1 text-lg md:text-xl font-extrabold tabular-nums text-[#0F8A5F] break-words">{formatCurrency(data.total_cobros)}</div>
          </div>
          <div className="rounded-lg p-3 bg-[var(--legatum-elevated)] border border-[var(--legatum-border)]">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--legatum-text-muted)]">Total pagos</div>
            <div className="mt-1 text-lg md:text-xl font-extrabold tabular-nums text-[#D85A1F] break-words">{formatCurrency(data.total_pagos)}</div>
          </div>
          <div className="rounded-lg p-3 bg-[var(--legatum-elevated)] border border-[var(--legatum-border)]">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--legatum-text-muted)]">Neto previsto</div>
            <div
              className="mt-1 text-lg md:text-xl font-extrabold tabular-nums break-words"
              style={{ color: (data.total_cobros - data.total_pagos) >= 0 ? "#0F8A5F" : "#DC2626" }}
            >
              {formatCurrency(data.total_cobros - data.total_pagos)}
            </div>
          </div>
          <div className="rounded-lg p-3 bg-[var(--legatum-elevated)] border border-[var(--legatum-border)]">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--legatum-text-muted)] flex items-center gap-1">
              {(data.vencidos_cobro > 0 || data.vencidos_pago > 0) && <AlertTriangle className="h-3 w-3 text-[#DC2626]" />}
              Vencidos
            </div>
            <div className="mt-1 text-[10px] text-[var(--legatum-text-secondary)]">
              <span className="font-bold text-[#0F8A5F]">{formatCurrency(data.vencidos_cobro)}</span>
              <span className="mx-1 text-[var(--legatum-text-muted)]">/</span>
              <span className="font-bold text-[#D85A1F]">{formatCurrency(data.vencidos_pago)}</span>
            </div>
            <div className="mt-0.5 text-[9px] text-[var(--legatum-text-muted)]">cobrar / pagar</div>
          </div>
        </div>

        {noData ? (
          <div className="text-center text-sm text-[var(--legatum-text-muted)] py-12">
            No hay facturas ni compras pendientes en el horizonte seleccionado.
          </div>
        ) : (
          <div className="h-64 md:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="var(--legatum-border)" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="mes"
                  tick={{ fill: "var(--legatum-text-muted)", fontSize: 11, fontWeight: 700 }}
                  tickLine={false}
                  axisLine={{ stroke: "var(--legatum-border)" }}
                />
                <YAxis
                  tickFormatter={fmtAxis}
                  tick={{ fill: "var(--legatum-text-muted)", fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: "var(--legatum-border)" }}
                />
                <Tooltip
                  contentStyle={{
                    background: "var(--legatum-surface)",
                    border: "1px solid var(--legatum-border)",
                    borderRadius: 8,
                    color: "var(--legatum-text)",
                  }}
                  formatter={(value) => formatCurrency(value)}
                  labelStyle={{ color: "var(--legatum-text-muted)", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11, fontWeight: 600, color: "var(--legatum-text-secondary)" }}
                  iconType="circle"
                />
                <Bar dataKey="Cobros" fill="url(#cobrosGrad)" radius={[6, 6, 0, 0]} maxBarSize={42} animationDuration={900} />
                <Bar dataKey="Pagos" fill="url(#pagosGrad)" radius={[6, 6, 0, 0]} maxBarSize={42} animationDuration={900} animationBegin={150} />
                <Line type="monotone" dataKey="Neto" stroke="var(--legatum-accent)" strokeWidth={3} dot={{ r: 4, fill: "var(--legatum-accent)", strokeWidth: 2, stroke: "var(--legatum-bg)" }} activeDot={{ r: 6 }} animationDuration={1200} />
                <defs>
                  <linearGradient id="cobrosGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0F8A5F" stopOpacity={1} />
                    <stop offset="100%" stopColor="#0F8A5F" stopOpacity={0.5} />
                  </linearGradient>
                  <linearGradient id="pagosGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D85A1F" stopOpacity={1} />
                    <stop offset="100%" stopColor="#D85A1F" stopOpacity={0.5} />
                  </linearGradient>
                </defs>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </motion.section>
  );
}
